
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export const useEditPackage = (id: string) => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to edit a package");
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate]);

  const { data: packageData, isLoading } = useQuery({
    queryKey: ['edit-package', id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('packages')
        .select('*, profiles(username)')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Package not found');

      // Check if the user is the creator
      if (data.user_id !== user.id) {
        throw new Error('Not authorized');
      }

      // Ensure recipes is an array and convert to strings
      const recipes = Array.isArray(data.recipes) ? data.recipes : [];
      
      // Transform the data to match the expected type
      return {
        id: data.id,
        title: data.title,
        description: data.description || "",
        visibility: data.visibility as "public" | "subscription",
        price: data.price || 0,
        recipes: recipes.map(String)
      };
    },
  });

  return { packageData, isLoading };
};
